import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { InputFieldWithCurrency } from '@/modules/common/components/molecules/inputs/input-field-with-currency';
import { SwitchField } from '@/modules/common/components/molecules/inputs/switch-field';

import {
  FORM_FIELD_ALLOW_NEGATIVE_BALANCE,
  FORM_FIELD_HAS_MAX_LIMIT,
  FORM_FIELD_MAX_LIMIT_DAYS,
  FORM_FIELD_NEGATIVE_BALANCE_DAYS,
} from '../../../../constants/time-off-type-form-fields';
import {
  MAX_LIMIT_DAYS_MAX_DECIMAL_PLACES,
  MAX_LIMIT_DAYS_MAX_INTEGER_DIGITS,
  MAX_LIMIT_DAYS_MAX_VALUE,
  MAX_LIMIT_DAYS_MIN_VALUE,
  NEGATIVE_BALANCE_DAYS_MAX_DECIMAL_PLACES,
  NEGATIVE_BALANCE_DAYS_MAX_INTEGER_DIGITS,
  NEGATIVE_BALANCE_DAYS_MAX_VALUE,
  NEGATIVE_BALANCE_DAYS_MIN_VALUE,
  TimeOffTypeSetupFormData,
} from '../../../../validations/time-off-type-validation';

interface BalanceSectionProps {
  form: UseFormReturn<TimeOffTypeSetupFormData>;
  isReadMode: boolean;
}

export function BalanceSection({ form, isReadMode }: BalanceSectionProps) {
  const { t } = useTranslation('authenticated.settings');
  const allowNegativeBalance = form.watch(FORM_FIELD_ALLOW_NEGATIVE_BALANCE);
  const hasMaxLimit = form.watch(FORM_FIELD_HAS_MAX_LIMIT);

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div>
          <h3 className="text-md font-semibold">
            {t('pages.timeOff.details.sections.balance.title')}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('pages.timeOff.details.sections.balance.description')}
          </p>
        </div>

        <SwitchField
          name={FORM_FIELD_ALLOW_NEGATIVE_BALANCE}
          control={form.control}
          label={t('pages.timeOff.details.sections.balance.allowNegative')}
          disabled={isReadMode}
          onCheckedChange={checked => {
            if (checked && form.getValues(FORM_FIELD_NEGATIVE_BALANCE_DAYS) == null) {
              form.setValue(FORM_FIELD_NEGATIVE_BALANCE_DAYS, NEGATIVE_BALANCE_DAYS_MIN_VALUE);
            }
          }}
        />

        {allowNegativeBalance && (
          <InputFieldWithCurrency
            name={FORM_FIELD_NEGATIVE_BALANCE_DAYS}
            label={t('pages.timeOff.details.sections.balance.negativeDays')}
            control={form.control}
            suffixText="days"
            isReadMode={isReadMode}
            maxIntegerDigits={NEGATIVE_BALANCE_DAYS_MAX_INTEGER_DIGITS}
            maxDecimalPlaces={NEGATIVE_BALANCE_DAYS_MAX_DECIMAL_PLACES}
            minValue={NEGATIVE_BALANCE_DAYS_MIN_VALUE}
            maxValue={NEGATIVE_BALANCE_DAYS_MAX_VALUE}
          />
        )}

        <SwitchField
          name={FORM_FIELD_HAS_MAX_LIMIT}
          control={form.control}
          label={t('pages.timeOff.details.sections.balance.maxLimit')}
          disabled={isReadMode}
          onCheckedChange={checked => {
            if (checked && form.getValues(FORM_FIELD_MAX_LIMIT_DAYS) == null) {
              form.setValue(FORM_FIELD_MAX_LIMIT_DAYS, MAX_LIMIT_DAYS_MIN_VALUE);
            }
          }}
        />

        {hasMaxLimit && (
          <InputFieldWithCurrency
            name={FORM_FIELD_MAX_LIMIT_DAYS}
            label={t('pages.timeOff.details.sections.balance.maxLimitDays')}
            control={form.control}
            suffixText="days"
            isReadMode={isReadMode}
            maxIntegerDigits={MAX_LIMIT_DAYS_MAX_INTEGER_DIGITS}
            maxDecimalPlaces={MAX_LIMIT_DAYS_MAX_DECIMAL_PLACES}
            minValue={MAX_LIMIT_DAYS_MIN_VALUE}
            maxValue={MAX_LIMIT_DAYS_MAX_VALUE}
          />
        )}
      </CardContent>
    </Card>
  );
}
